/**
 * Fast Feature Management Service (PBI 481284)
 *
 * All status / toggle / metadata functions call real API endpoints.
 * getFastFeatureDetails / saveFastFeatureDetails map between the backend
 * metadata row schema and the UI output-slot schema.
 *
 * Real endpoints:
 *   GET    /features/fast-feature-metadata/status/{featureId}
 *   POST   /features/fast-feature-metadata/toggle/{featureId}
 *   GET    /features/fast-feature-metadata
 *   GET    /features/fast-feature-metadata/by-feature/{featureId}
 *   POST   /features/fast-feature-metadata/create
 *   POST   /features/fast-feature-metadata/{featureId}/update
 *   POST   /features/fast-feature-metadata/{metaId}/delete
 */

import { apiRequest } from 'utils/request'

// The Redux store keys features by "featureId:revision" — strip the suffix
// before using featureId in any API URL.
const toApiId = (featureId) =>
  featureId ? String(featureId).split(':')[0] : featureId

// ─── 35M code identifiers (mirrors constants.js, avoids circular dep) ─────────
const CODES_35M = [
  '35M-010',
  '35M-011',
  '35M-012',
  '35M-013',
  '35M-014',
  '35M-015',
]

// ─── Available options (no backend endpoint — kept as static reference data) ──

// Per-connector, per-output-type 35M character code options.
// Source: "Fast Feature Metadata.xlsx" sheets 35M-010 & -014, 35M-011,
//         35M-012, 35M-013, 35M-015.
// Keys map to UI apiOutputType values: '20A', '5A', '2A', 'ECP'.
const CODES_35M_BY_CONNECTOR = {
  '35M-010': {
    '20A': [
      { value: 'A1', label: 'A1 (DO1)' },
      { value: 'A2', label: 'A2 (DO4)' },
      { value: 'A3', label: 'A3 (DO5)' },
      { value: 'A4', label: 'A4 (DO6)' },
      { value: 'AF', label: 'AF (DO1+DO4)' },
      { value: 'AG', label: 'AG (DO5+DO6)' },
    ],
    '5A': [
      { value: 'B1', label: 'B1 (DO7)' },
      { value: 'B2', label: 'B2 (DO8)' },
      { value: 'B3', label: 'B3 (DO9)' },
      { value: 'B4', label: 'B4 (DO10)' },
      { value: 'BA', label: 'BA (DO7+DO8)' },
      { value: 'BB', label: 'BB (DO9+DO10)' },
    ],
    '2A': [
      { value: 'C1', label: 'C1 (DO11)' },
      { value: 'C2', label: 'C2 (DO12)' },
      { value: 'C3', label: 'C3 (DO13)' },
      { value: 'C4', label: 'C4 (DO14)' },
      { value: 'C5', label: 'C5 (DO15)' },
      { value: 'C6', label: 'C6 (DO16)' },
      { value: 'C7', label: 'C7 (DO17)' },
      { value: 'C8', label: 'C8 (DO18)' },
      { value: 'CA', label: 'CA (DO11+DO12)' },
      { value: 'CB', label: 'CB (DO13+DO14)' },
      { value: 'CC', label: 'CC (DO15+DO16)' },
      { value: 'CD', label: 'CD (DO17+DO18)' },
    ],
    ECP: [
      { value: 'D1', label: 'D1 (ECP 1)' },
      { value: 'D2', label: 'D2 (ECP 2)' },
      { value: 'D3', label: 'D3 (ECP 3)' },
      { value: 'D4', label: 'D4 (ECP 4)' },
      { value: 'D5', label: 'D5 (ECP 5)' },
      { value: 'D6', label: 'D6 (ECP 6)' },
      { value: 'DA', label: 'DA (ECP 1+2)' },
      { value: 'DB', label: 'DB (ECP 3+4)' },
      { value: 'DC', label: 'DC (ECP 5+6)' },
    ],
  },
  '35M-011': {
    '20A': [],
    '5A': [
      { value: 'B1', label: 'B1 (DO7)' },
      { value: 'B2', label: 'B2 (DO8)' },
      { value: 'B3', label: 'B3 (DO9)' },
      { value: 'B4', label: 'B4 (DO10)' },
      { value: 'BA', label: 'BA (DO7+DO8)' },
      { value: 'BB', label: 'BB (DO9+DO10)' },
    ],
    '2A': [
      { value: 'A1', label: 'A1 (DO11)' },
      { value: 'A2', label: 'A2 (DO12)' },
      { value: 'A3', label: 'A3 (DO13)' },
      { value: 'A4', label: 'A4 (DO14)' },
      { value: 'AF', label: 'AF (DO11+DO12)' },
      { value: 'AG', label: 'AG (DO13+DO14)' },
      { value: 'C1', label: 'C1 (DO15)' },
      { value: 'C2', label: 'C2 (DO16)' },
      { value: 'C3', label: 'C3 (DO17)' },
      { value: 'C4', label: 'C4 (DO18)' },
      { value: 'CA', label: 'CA (DO15+DO16)' },
      { value: 'CB', label: 'CB (DO17+DO18)' },
    ],
    ECP: [
      { value: 'D1', label: 'D1 (ECP 1)' },
      { value: 'D2', label: 'D2 (ECP 2)' },
      { value: 'D3', label: 'D3 (ECP 3)' },
      { value: 'D4', label: 'D4 (ECP 4)' },
      { value: 'D5', label: 'D5 (ECP 5)' },
      { value: 'D6', label: 'D6 (ECP 6)' },
      { value: 'DA', label: 'DA (ECP 1+2)' },
      { value: 'DB', label: 'DB (ECP 3+4)' },
      { value: 'DC', label: 'DC (ECP 5+6)' },
    ],
  },
  '35M-012': {
    '20A': [
      { value: 'A1', label: 'A1 (DO1)' },
      { value: 'A2', label: 'A2 (DO2)' },
      { value: 'A3', label: 'A3 (DO3)' },
      { value: 'A4', label: 'A4 (DO4)' },
      { value: 'A5', label: 'A5 (DO5)' },
      { value: 'A6', label: 'A6 (DO6)' },
      { value: 'AF', label: 'AF (DO1+DO2)' },
      { value: 'AG', label: 'AG (DO3+DO4)' },
      { value: 'AH', label: 'AH (DO5+DO6)' },
    ],
    '5A': [
      { value: 'B1', label: 'B1 (DO7)' },
      { value: 'B2', label: 'B2 (DO8)' },
      { value: 'B3', label: 'B3 (DO9)' },
      { value: 'B4', label: 'B4 (DO10)' },
      { value: 'BA', label: 'BA (DO7+DO8)' },
      { value: 'BB', label: 'BB (DO9+DO10)' },
    ],
    '2A': [
      { value: 'A7', label: 'A7 (DO11)' },
      { value: 'A8', label: 'A8 (DO12)' },
      { value: 'A9', label: 'A9 (DO13)' },
      { value: 'AA', label: 'AA (DO14)' },
      { value: 'AB', label: 'AB (DO15)' },
      { value: 'AC', label: 'AC (DO16)' },
      { value: 'AD', label: 'AD (DO17)' },
      { value: 'AE', label: 'AE (DO18)' },
      { value: 'AI', label: 'AI (DO11+DO12)' },
      { value: 'AJ', label: 'AJ (DO13+DO14)' },
      { value: 'AK', label: 'AK (DO15+DO16)' },
      { value: 'AL', label: 'AL (DO17+DO18)' },
    ],
    ECP: [],
  },
  '35M-013': {
    '20A': [
      { value: 'A1', label: 'A1 (DO1)' },
      { value: 'A2', label: 'A2 (DO4)' },
      { value: 'A3', label: 'A3 (DO5)' },
      { value: 'A4', label: 'A4 (DO6)' },
      { value: 'AF', label: 'AF (DO1+DO4)' },
      { value: 'AG', label: 'AG (DO5+DO6)' },
      { value: 'E1', label: 'E1 (DO1)' },
      { value: 'E2', label: 'E2 (DO4)' },
      { value: 'E3', label: 'E3 (DO5)' },
      { value: 'E4', label: 'E4 (DO6)' },
      { value: 'EF', label: 'EF (DO1+DO4)' },
      { value: 'EG', label: 'EG (DO5+DO6)' },
    ],
    '5A': [
      { value: 'B1', label: 'B1 (DO7)' },
      { value: 'B2', label: 'B2 (DO8)' },
      { value: 'B3', label: 'B3 (DO9)' },
      { value: 'B4', label: 'B4 (DO10)' },
      { value: 'BA', label: 'BA (DO7+DO8)' },
      { value: 'BB', label: 'BB (DO9+DO10)' },
      { value: 'F1', label: 'F1 (DO7)' },
      { value: 'F2', label: 'F2 (DO8)' },
      { value: 'F3', label: 'F3 (DO9)' },
      { value: 'F4', label: 'F4 (DO10)' },
      { value: 'FA', label: 'FA (DO7+DO8)' },
      { value: 'FB', label: 'FB (DO9+DO10)' },
    ],
    '2A': [
      { value: 'C1', label: 'C1 (DO11)' },
      { value: 'C2', label: 'C2 (DO12)' },
      { value: 'C3', label: 'C3 (DO13)' },
      { value: 'C4', label: 'C4 (DO14)' },
      { value: 'C5', label: 'C5 (DO15)' },
      { value: 'C6', label: 'C6 (DO16)' },
      { value: 'C7', label: 'C7 (DO17)' },
      { value: 'C8', label: 'C8 (DO18)' },
      { value: 'CA', label: 'CA (DO11+DO12)' },
      { value: 'CB', label: 'CB (DO13+DO14)' },
      { value: 'CC', label: 'CC (DO15+DO16)' },
      { value: 'CD', label: 'CD (DO17+DO18)' },
      { value: 'G1', label: 'G1 (DO11)' },
      { value: 'G2', label: 'G2 (DO12)' },
      { value: 'G3', label: 'G3 (DO13)' },
      { value: 'G4', label: 'G4 (DO14)' },
      { value: 'G5', label: 'G5 (DO15)' },
      { value: 'G6', label: 'G6 (DO16)' },
      { value: 'G7', label: 'G7 (DO17)' },
      { value: 'G8', label: 'G8 (DO18)' },
      { value: 'GA', label: 'GA (DO11+DO12)' },
      { value: 'GB', label: 'GB (DO13+DO14)' },
      { value: 'GC', label: 'GC (DO15+DO16)' },
      { value: 'GD', label: 'GD (DO17+DO18)' },
    ],
    ECP: [
      { value: 'D1', label: 'D1 (ECP 1)' },
      { value: 'D2', label: 'D2 (ECP 2)' },
      { value: 'D3', label: 'D3 (ECP 3)' },
      { value: 'D4', label: 'D4 (ECP 4)' },
      { value: 'D5', label: 'D5 (ECP 5)' },
      { value: 'D6', label: 'D6 (ECP 6)' },
      { value: 'DA', label: 'DA (ECP 1+2)' },
      { value: 'DB', label: 'DB (ECP 3+4)' },
      { value: 'DC', label: 'DC (ECP 5+6)' },
      { value: 'H1', label: 'H1 (ECP 1)' },
      { value: 'H2', label: 'H2 (ECP 2)' },
      { value: 'H3', label: 'H3 (ECP 3)' },
      { value: 'H4', label: 'H4 (ECP 4)' },
      { value: 'H5', label: 'H5 (ECP 5)' },
      { value: 'H6', label: 'H6 (ECP 6)' },
      { value: 'HA', label: 'HA (ECP 1+2)' },
      { value: 'HB', label: 'HB (ECP 3+4)' },
      { value: 'HC', label: 'HC (ECP 5+6)' },
    ],
  },
  '35M-015': {
    '20A': [
      { value: 'E1', label: 'E1 (DO1)' },
      { value: 'E2', label: 'E2 (DO4)' },
      { value: 'E3', label: 'E3 (DO5)' },
      { value: 'E4', label: 'E4 (DO6)' },
      { value: 'EF', label: 'EF (DO1+DO4)' },
      { value: 'EG', label: 'EG (DO5+DO6)' },
    ],
    '5A': [
      { value: 'B1', label: 'B1 (DO7)' },
      { value: 'B2', label: 'B2 (DO8)' },
      { value: 'B3', label: 'B3 (DO9)' },
      { value: 'B4', label: 'B4 (DO10)' },
      { value: 'BA', label: 'BA (DO7+DO8)' },
      { value: 'BB', label: 'BB (DO9+DO10)' },
      { value: 'F1', label: 'F1 (DO7)' },
      { value: 'F2', label: 'F2 (DO8)' },
      { value: 'F3', label: 'F3 (DO9)' },
      { value: 'F4', label: 'F4 (DO10)' },
      { value: 'FA', label: 'FA (DO7+DO8)' },
      { value: 'FB', label: 'FB (DO9+DO10)' },
    ],
    '2A': [
      { value: 'A1', label: 'A1 (DO11)' },
      { value: 'A2', label: 'A2 (DO12)' },
      { value: 'A3', label: 'A3 (DO13)' },
      { value: 'A4', label: 'A4 (DO14)' },
      { value: 'AF', label: 'AF (DO11+DO12)' },
      { value: 'AG', label: 'AG (DO13+DO14)' },
      { value: 'C1', label: 'C1 (DO15)' },
      { value: 'C2', label: 'C2 (DO16)' },
      { value: 'C3', label: 'C3 (DO17)' },
      { value: 'C4', label: 'C4 (DO18)' },
      { value: 'CA', label: 'CA (DO15+DO16)' },
      { value: 'CB', label: 'CB (DO17+DO18)' },
      { value: 'G1', label: 'G1 (DO11)' },
      { value: 'G2', label: 'G2 (DO12)' },
      { value: 'G3', label: 'G3 (DO13)' },
      { value: 'G4', label: 'G4 (DO14)' },
      { value: 'G5', label: 'G5 (DO15)' },
      { value: 'G6', label: 'G6 (DO16)' },
      { value: 'G7', label: 'G7 (DO17)' },
      { value: 'G8', label: 'G8 (DO18)' },
      { value: 'GA', label: 'GA (DO11+DO12)' },
      { value: 'GB', label: 'GB (DO13+DO14)' },
      { value: 'GC', label: 'GC (DO15+DO16)' },
      { value: 'GD', label: 'GD (DO17+DO18)' },
    ],
    ECP: [
      { value: 'D1', label: 'D1 (ECP 1)' },
      { value: 'D2', label: 'D2 (ECP 2)' },
      { value: 'D3', label: 'D3 (ECP 3)' },
      { value: 'D4', label: 'D4 (ECP 4)' },
      { value: 'D5', label: 'D5 (ECP 5)' },
      { value: 'D6', label: 'D6 (ECP 6)' },
      { value: 'DA', label: 'DA (ECP 1+2)' },
      { value: 'DB', label: 'DB (ECP 3+4)' },
      { value: 'DC', label: 'DC (ECP 5+6)' },
      { value: 'H1', label: 'H1 (ECP 1)' },
      { value: 'H2', label: 'H2 (ECP 2)' },
      { value: 'H3', label: 'H3 (ECP 3)' },
      { value: 'H4', label: 'H4 (ECP 4)' },
      { value: 'H5', label: 'H5 (ECP 5)' },
      { value: 'H6', label: 'H6 (ECP 6)' },
      { value: 'HA', label: 'HA (ECP 1+2)' },
      { value: 'HB', label: 'HB (ECP 3+4)' },
      { value: 'HC', label: 'HC (ECP 5+6)' },
    ],
  },
}
// 35M-014 shares the same options as 35M-010
CODES_35M_BY_CONNECTOR['35M-014'] = CODES_35M_BY_CONNECTOR['35M-010']

const AVAILABLE_OPTIONS = {
  pins: Array.from({ length: 18 }, (_, i) => ({
    value: `digital_output_${i + 1}`,
    label: `Digital Output ${i + 1}`,
  })),
  solenoids: [
    { value: 'optional_1', label: 'Optional 1' },
    { value: 'optional_2', label: 'Optional 2' },
    { value: 'optional_3', label: 'Optional 3' },
    { value: 'optional_4', label: 'Optional 4' },
    { value: 'optional_5', label: 'Optional 5' },
    { value: 'optional_6', label: 'Optional 6' },
  ],
  indicators: Array.from({ length: 60 }, (_, i) => {
    const num = String(i).padStart(2, '0')
    return { value: `temsw_${num}`, label: `TEMSw ${num} Switch Indicator` }
  }),
  switches: Array.from({ length: 60 }, (_, i) => {
    const num = String(i).padStart(2, '0')
    return { value: `tem_switch_${num}`, label: `TEM Custom Switch ${num}` }
  }),
  codes35MByConnector: CODES_35M_BY_CONNECTOR,
}

// ─── Mapping: API ↔ UI ────────────────────────────────────────────────────────

// API outputType "2A"|"5A"|"20A" → UI "PIN", "ECP" → "ECP", "SWI" → "SWITCH_INDICATOR"
const apiOutputTypeToUi = (apiType) => {
  if (!apiType) return null
  if (['2A', '5A', '20A'].includes(apiType)) return 'PIN'
  if (apiType === 'ECP') return 'ECP'
  if (apiType === 'SWI') return 'SWITCH_INDICATOR'
  return null
}

// "DO11" → "digital_output_11"
const apiOutputNumberToUi = (outputNumber) => {
  if (!outputNumber) return null
  const match = outputNumber.match(/^DO(\d+)$/i)
  return match ? `digital_output_${match[1]}` : outputNumber
}

// "Optional 1" → "optional_1"
const apiSolenoidToUi = (solenoid) => {
  if (!solenoid) return null
  return solenoid.toLowerCase().replace(/\s+/g, '_')
}

// switchNumber 0 → "tem_switch_00"
const apiSwitchToUiDetail = (switchNumber) => {
  if (switchNumber === null || switchNumber === undefined) return null
  return `tem_switch_${String(switchNumber).padStart(2, '0')}`
}

// SWI switchNumber 0 → "temsw_00"
const apiIndicatorToUiDetail = (switchNumber) => {
  if (switchNumber === null || switchNumber === undefined) return null
  return `temsw_${String(switchNumber).padStart(2, '0')}`
}

// Build an empty UI output slot
const buildEmptyUiOutput = (slotIndex) => ({
  outputNumber: slotIndex + 1,
  outputType: null,
  outputDetail: null,
  inputType: null,
  inputDetail: null,
  codes35M: CODES_35M.reduce((acc, code) => ({ ...acc, [code]: null }), {}),
})

// API metadata row → UI output slot
// metaId, apiOutputType, xmcModule are internal fields used for round-trip
const apiRowToUiOutput = (row, slotIndex) => {
  const outputType = apiOutputTypeToUi(row.outputType)
  let outputDetail = null
  if (outputType === 'PIN') outputDetail = apiOutputNumberToUi(row.outputNumber)
  else if (outputType === 'ECP') outputDetail = apiSolenoidToUi(row.solenoid)
  else if (outputType === 'SWITCH_INDICATOR')
    outputDetail = apiIndicatorToUiDetail(row.switchNumber)

  // For SWITCH_INDICATOR the switchNumber field stores the indicator — not an input switch
  const inputType =
    outputType === 'SWITCH_INDICATOR'
      ? 'N/A'
      : row.switchNumber != null
      ? 'SWITCH'
      : 'N/A'
  const inputDetail =
    outputType === 'SWITCH_INDICATOR'
      ? null
      : apiSwitchToUiDetail(row.switchNumber)

  return {
    metaId: row.metaId,
    apiOutputType: ['2A', '5A', '20A', 'ECP', 'SWI'].includes(row.outputType)
      ? row.outputType
      : null,
    xmcModule: row.xmcModule && row.xmcModule !== 'PEND' ? row.xmcModule : null,
    outputNumber: slotIndex + 1,
    outputType,
    outputDetail,
    inputType,
    inputDetail,
    codes35M: CODES_35M.reduce(
      (acc, code) => ({
        ...acc,
        [code]: code === row.module35MCode ? row.module35MCodeValue : null,
      }),
      {}
    ),
  }
}

// New detail API output → UI output slot
// Response shape per output (new flat format):
//   { outputType: "Pin"|"SWI"|"ECP", outputNumber: "1",
//     outputDetails: "Digital Output 6",
//     "35M-010": "C1", ..., "35M-015": "C1",
//     inputType: "Switch"|"N/A", inputDetails: "TEM Custom Switch 0"|"N/A" }
// Legacy format (still supported):
//   { outputType: "Pin"|"Indicator"|"Ecp", pinAmps, pinDescription,
//     switchNumber, solenoid, xmcModule,
//     modules: [{ metaId, module35MCode, module35MCodeValue }] }
const apiDetailOutputToUiOutput = (
  output,
  slotIndex,
  topLevelFilter,
  topLevelInput
) => {
  const {
    outputType: rawType,
    outputNumber: apiOutputNumber,
    outputDetails,
    pinAmps,
    pinDescription,
    switchNumber,
    solenoid,
    xmcModule,
    modules = [],
  } = output

  // Detect new flat format: has outputDetails or flat 35M keys
  const isNewFlatFormat =
    outputDetails !== undefined || CODES_35M.some((code) => code in output)

  let uiOutputType = null
  let apiOutputType = null
  const type = rawType ? rawType.toLowerCase() : ''
  if (type === 'pin') {
    uiOutputType = 'PIN'
    if (isNewFlatFormat) {
      apiOutputType = topLevelFilter || '2A'
    } else if (pinAmps >= 20) {
      apiOutputType = '20A'
    } else if (pinAmps >= 5) {
      apiOutputType = '5A'
    } else {
      apiOutputType = '2A'
    }
  } else if (type === '20a') {
    uiOutputType = 'PIN'
    apiOutputType = '20A'
  } else if (type === '5a') {
    uiOutputType = 'PIN'
    apiOutputType = '5A'
  } else if (type === '2a') {
    uiOutputType = 'PIN'
    apiOutputType = '2A'
  } else if (type === 'indicator' || type === 'swi') {
    uiOutputType = 'SWITCH_INDICATOR'
    apiOutputType = 'SWI'
  } else if (type === 'ecp') {
    uiOutputType = 'ECP'
    apiOutputType = 'ECP'
  }

  let outputDetail = null
  if (isNewFlatFormat && outputDetails) {
    if (uiOutputType === 'PIN') {
      // "Digital Output 6" → "digital_output_6"
      const mLong = outputDetails.match(/digital\s+output\s+0*(\d+)/i)
      if (mLong) {
        outputDetail = `digital_output_${parseInt(mLong[1], 10)}`
      } else {
        // "DO6", "DO6, 5A", "DO9 + DO10" — extract first DO number
        const mDO = outputDetails.match(/DO0*(\d+)/i)
        if (mDO) {
          outputDetail = `digital_output_${parseInt(mDO[1], 10)}`
        } else {
          outputDetail = apiOutputNumberToUi(outputDetails)
        }
      }

      // Extract amp rating from outputDetails if present (e.g. "DO6, 5A")
      const ampMatch = outputDetails.match(/\b(2|5|20)A\b/i)
      if (ampMatch) {
        apiOutputType = `${ampMatch[1]}A`
      }
    } else if (uiOutputType === 'SWITCH_INDICATOR') {
      // "TEMSw 00 Switch Indicator" or "TEMSw 00" → "temsw_00"
      const m = outputDetails.match(/temsw\s+0*(\d+)/i)
      outputDetail = m
        ? `temsw_${String(parseInt(m[1], 10)).padStart(2, '0')}`
        : null
    } else if (uiOutputType === 'ECP') {
      // "TEM Optional Air Solenoid 1" → "optional_1"
      const mSol = outputDetails.match(/solenoid\s+0*(\d+)/i)
      outputDetail = mSol
        ? `optional_${parseInt(mSol[1], 10)}`
        : apiSolenoidToUi(outputDetails)
    }
  } else if (uiOutputType === 'PIN') {
    if (pinDescription) {
      const m = pinDescription.match(/digital\s+output\s+0*(\d+)/i)
      outputDetail = m ? `digital_output_${parseInt(m[1], 10)}` : null
    } else {
      outputDetail = apiOutputNumberToUi(apiOutputNumber)
    }
  } else if (uiOutputType === 'SWITCH_INDICATOR' && pinDescription) {
    const m = pinDescription.match(/temsw\s+0*(\d+)/i)
    outputDetail = m
      ? `temsw_${String(parseInt(m[1], 10)).padStart(2, '0')}`
      : null
  } else if (uiOutputType === 'ECP') {
    outputDetail = apiSolenoidToUi(solenoid)
  }

  let inputType
  let inputDetail
  // Prefer per-output inputType/inputDetails; fall back to top-level for
  // backward compatibility with responses that only have top-level fields.
  const perOutputInput =
    isNewFlatFormat && output.inputType != null
      ? { inputType: output.inputType, inputDetails: output.inputDetails }
      : null
  const effectiveInput = perOutputInput || topLevelInput

  if (isNewFlatFormat && effectiveInput) {
    const rawInput = effectiveInput.inputType
      ? effectiveInput.inputType.toLowerCase()
      : ''
    if (rawInput === 'switch') {
      inputType = 'SWITCH'
      const details = effectiveInput.inputDetails
      if (details && details !== 'N/A') {
        const m = details.match(/switch\s+0*(\d+)/i)
        inputDetail = m
          ? `tem_switch_${String(parseInt(m[1], 10)).padStart(2, '0')}`
          : null
      } else {
        inputDetail = null
      }
    } else {
      inputType = 'N/A'
      inputDetail = null
    }
  } else {
    // Legacy format
    inputType =
      uiOutputType === 'SWITCH_INDICATOR'
        ? 'N/A'
        : switchNumber != null
        ? 'SWITCH'
        : 'N/A'
    inputDetail =
      uiOutputType === 'SWITCH_INDICATOR'
        ? null
        : apiSwitchToUiDetail(switchNumber)
  }

  // Build codes35M from flat keys (new format) or modules array (legacy)
  const codes35M = CODES_35M.reduce(
    (acc, code) => ({ ...acc, [code]: null }),
    {}
  )
  if (isNewFlatFormat) {
    CODES_35M.forEach((code) => {
      if (output[code] != null && output[code] !== 'N/A') {
        codes35M[code] = output[code]
      }
    })
  } else {
    modules.forEach(({ module35MCode, module35MCodeValue }) => {
      if (module35MCode && CODES_35M.includes(module35MCode)) {
        codes35M[module35MCode] = module35MCodeValue
      }
    })
  }

  // Use the API's outputNumber for slot placement when it's a valid 1-5 value;
  // fall back to the array index for legacy rows that lack a slot number.
  const parsedSlot = parseInt(apiOutputNumber, 10)
  const resolvedOutputNumber =
    parsedSlot >= 1 && parsedSlot <= 5 ? parsedSlot : slotIndex + 1

  return {
    modules, // stored for round-trip: [{ metaId, module35MCode }]
    apiOutputType,
    xmcModule: xmcModule && xmcModule !== 'PEND' ? xmcModule : null,
    outputNumber: resolvedOutputNumber,
    outputType: uiOutputType,
    outputDetail,
    inputType,
    inputDetail,
    codes35M,
  }
}

// ─── API: Fast Feature Status ─────────────────────────────────────────────────

/**
 * GET /features/{featureId}/fast-feature-status
 * Returns: { featureId, isFastFeature, outputCount }
 */
export const getFastFeatureStatus = ({ featureId }) =>
  apiRequest({
    uri: `/features/fast-feature-metadata/status/${toApiId(featureId)}`,
  })

// ─── API: Toggle Fast Feature ─────────────────────────────────────────────────

/**
 * POST /features/{featureId}/toggle-fast-feature
 * Body: { enabled: boolean }
 * Returns: { featureId, isFastFeature }
 */
export const setFastFeatureStatus = ({ featureId, isFastFeature }) =>
  apiRequest({
    uri: `/features/fast-feature-metadata/toggle/${toApiId(featureId)}`,
    method: 'POST',
    body: { enabled: isFastFeature },
  })

// ─── API: Search / List Metadata ──────────────────────────────────────────────

/**
 * GET /features/fast-feature-metadata
 * Supports: offset, limit, searchText, ecuType, outputType, solenoid, switchNumber
 * Returns: { items, total, offset, limit }
 */
export const searchFastFeatureMetadata = (params = {}) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&')
  const uri = qs
    ? `/features/fast-feature-metadata?${qs}`
    : '/features/fast-feature-metadata'
  return apiRequest({ uri })
}

// ─── API: Fast Feature Details (modal load) ───────────────────────────────────

/**
 * GET /features/fast-feature-metadata/by-feature/{featureId}
 * Maps API metadata rows to the UI output-slot format (max 5 slots).
 * availableOptions are kept as static reference data (no backend endpoint).
 */
export const getFastFeatureDetails = async ({ featureId }) => {
  const outputs = Array.from({ length: 5 }, (_, i) => buildEmptyUiOutput(i))
  let fastFeatureFilter = null

  try {
    const response = await apiRequest({
      uri: `/features/fast-feature-metadata/by-feature/${toApiId(featureId)}`,
    })

    // New format: { data: { featureId, outputs: [...] } }
    // Old format: plain array or { data: [...] } or { data: { items: [...] } }
    const dataBlock = response?.data ?? response
    const isNewFormat =
      dataBlock && !Array.isArray(dataBlock) && Array.isArray(dataBlock.outputs)

    // Extract fastFeatureFilter first so it can inform output mapping
    if (dataBlock?.fastFeatureFilter != null) {
      fastFeatureFilter = dataBlock.fastFeatureFilter
    } else {
      const rows = isNewFormat
        ? dataBlock.outputs
        : Array.isArray(dataBlock)
        ? dataBlock
        : dataBlock?.items || []
      const firstWithFilter = rows.find((r) => r.fastFeatureFilter != null)
      if (firstWithFilter) {
        fastFeatureFilter = firstWithFilter.fastFeatureFilter
      }
    }

    // Extract top-level inputType/inputDetails for new format
    const topLevelInput = isNewFormat
      ? { inputType: dataBlock.inputType, inputDetails: dataBlock.inputDetails }
      : null

    if (isNewFormat) {
      // Place each output into the slot matching its outputNumber (1-5).
      // This handles sparse responses (e.g., only output 3) and out-of-order
      // responses without mis-assigning tiles.
      dataBlock.outputs.forEach((o, i) => {
        const mapped = apiDetailOutputToUiOutput(
          o,
          i,
          fastFeatureFilter,
          topLevelInput
        )
        const slot = mapped.outputNumber - 1
        if (slot >= 0 && slot < 5) {
          outputs[slot] = mapped
        }
      })
    } else {
      const rows = Array.isArray(dataBlock)
        ? dataBlock
        : Array.isArray(dataBlock?.items)
        ? dataBlock.items
        : []
      rows.slice(0, 5).forEach((row, i) => {
        outputs[i] = apiRowToUiOutput(row, i)
      })
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(
      '[FastFeature] Failed to load details for feature',
      featureId,
      err
    )
  }

  return {
    data: {
      featureId,
      outputs,
      availableOptions: AVAILABLE_OPTIONS,
      fastFeatureFilter,
    },
  }
}

// ─── API: Save Fast Feature Details (modal save) ──────────────────────────────

// Convert UI output slot → API output shape for the new flat format
const uiOutputToApiOutput = (output, filterOutput) => {
  const {
    outputNumber,
    outputType,
    outputDetail,
    inputType,
    inputDetail,
    codes35M,
  } = output

  if (!outputType || outputType === 'N/A') return null

  // Map UI outputType back to API value
  let apiOutputType = outputType
  if (outputType === 'PIN') apiOutputType = 'Pin'
  else if (outputType === 'SWITCH_INDICATOR') apiOutputType = 'SWI'
  else if (outputType === 'ECP') apiOutputType = 'ECP'

  // Map UI outputDetail back to human-readable outputDetails
  let outputDetails = null
  if (outputType === 'PIN' && outputDetail) {
    const m = outputDetail.match(/^digital_output_(\d+)$/)
    outputDetails = m ? `Digital Output ${m[1]}` : outputDetail
  } else if (outputType === 'SWITCH_INDICATOR' && outputDetail) {
    const m = outputDetail.match(/^temsw_(\d+)$/)
    outputDetails = m ? `TEMSw ${m[1]} Switch Indicator` : outputDetail
  } else if (outputType === 'ECP' && outputDetail) {
    const mSol = outputDetail.match(/^optional_(\d+)$/)
    outputDetails = mSol ? `TEM Optional Air Solenoid ${mSol[1]}` : outputDetail
  }

  // Extract solenoid number for ECP outputs
  let solenoid = null
  if (outputType === 'ECP' && outputDetail) {
    const mSol = outputDetail.match(/^optional_(\d+)$/)
    if (mSol) solenoid = parseInt(mSol[1], 10)
  }

  // Map input type/details per output (backend expects these per-output)
  let apiInputType = 'N/A'
  let apiInputDetails = 'N/A'
  if (inputType === 'SWITCH' && inputDetail) {
    apiInputType = 'Switch'
    const m = inputDetail.match(/^tem_switch_(\d+)$/)
    if (m) {
      apiInputDetails = `TEM Custom Switch ${String(
        parseInt(m[1], 10)
      ).padStart(2, '0')}`
    } else {
      apiInputDetails = inputDetail
    }
  } else if (inputType === 'SWITCH') {
    apiInputType = 'Switch'
  }

  // Build flat 35M code entries
  const codes35MFlat = {}
  CODES_35M.forEach((code) => {
    codes35MFlat[code] = codes35M?.[code] ?? null
  })

  return {
    outputType: apiOutputType,
    outputNumber: String(outputNumber),
    outputDetails,
    solenoid,
    inputType: apiInputType,
    inputDetails: apiInputDetails,
    ...codes35MFlat,
  }
}

/**
 * POST /features/fast-feature-metadata/{featureId}/update
 * Sends the full outputs array in the new flat format.
 */
export const saveFastFeatureDetails = async ({
  featureId,
  featureNumber,
  description,
  ecuType,
  outputs,
  filterOutput,
}) => {
  const apiOutputs = outputs
    .map((o) => uiOutputToApiOutput(o, filterOutput))
    .filter(Boolean)

  // Extract inputType/inputDetails from any output that has a switch configured
  const switchOutput = outputs.find(
    (o) => o.outputType && o.outputType !== 'N/A' && o.inputType === 'SWITCH'
  )
  let apiInputType = 'N/A'
  let apiInputDetails = 'N/A'
  let apiSwitchNumber = null
  if (switchOutput) {
    apiInputType = 'Switch'
    if (switchOutput.inputDetail) {
      const m = switchOutput.inputDetail.match(/^tem_switch_(\d+)$/)
      if (m) {
        apiSwitchNumber = parseInt(m[1], 10)
        apiInputDetails = `TEM Custom Switch ${String(apiSwitchNumber).padStart(
          2,
          '0'
        )}`
      } else {
        apiInputDetails = switchOutput.inputDetail
      }
    }
  }

  const body = {
    featureId: parseInt(toApiId(featureId), 10),
    featureNumber: featureNumber || '',
    description: description || '',
    ecuType: ecuType || '',
    fastFeatureFilter: filterOutput,
    inputType: apiInputType,
    inputDetails: apiInputDetails,
    switchNumber: apiSwitchNumber,
    switchPn:
      apiSwitchNumber != null
        ? `-${String(apiSwitchNumber).padStart(3, '0')}`
        : '',
    outputs: apiOutputs,
  }

  await apiRequest({
    uri: `/features/fast-feature-metadata/${toApiId(featureId)}/update`,
    method: 'POST',
    body,
  })

  return { data: { featureId, outputs, filterOutput } }
}
